import { forwardRef, useMemo } from 'react'
import { X } from 'lucide-react'
import { GestorDashboardBreakdownList, GestorDashboardMetricCard } from '@/components/Gestores/GestorDashboardWidgets'
import { GestorDashboardTimeTable } from '@/components/Gestores/GestorDashboardTimeTable'
import { formatEffortMinutes } from '@/components/Gestores/gestorDashboardFormat'
import {
  aggregateClientScopeBreakdown,
  buildClientScopeTaskRows,
  summarizeClientScopeEntries,
} from '@/components/Gestores/gestorDashboardClientScopeUtils'
import type { GestorDashboardTimeEntry } from '@/components/Gestores/gestorDashboardTypes'

type ScopeKind = 'workspace' | 'phase'

type Props = {
  scope: ScopeKind
  label: string
  entries: GestorDashboardTimeEntry[]
  loading?: boolean
  onClear: () => void
  onTaskOpen: (taskId: number) => void
}

const scopeCopy: Record<ScopeKind, { badge: string; subtitle: string }> = {
  workspace: {
    badge: 'Cliente selecionado',
    subtitle: 'Tarefas e apontamentos segmentados por cliente no período filtrado.',
  },
  phase: {
    badge: 'Etapa selecionada',
    subtitle: 'Tarefas e apontamentos segmentados por etapa no período filtrado.',
  },
}

export const GestorDashboardClientScopePanel = forwardRef<HTMLElement, Props>(function GestorDashboardClientScopePanel(
  { scope, label, entries, loading = false, onClear, onTaskOpen },
  ref,
) {
  const summary = summarizeClientScopeEntries(entries)
  const copy = scopeCopy[scope]

  const taskRows = useMemo(
    () =>
      buildClientScopeTaskRows(entries).map((row) => ({
        key: row.key,
        label: row.workspace ? `${row.task} · ${row.workspace}` : row.task,
        taskId: row.taskId,
        effortMinutes: row.effortMinutes,
      })),
    [entries],
  )

  const byPhaseRows = useMemo(
    () =>
      aggregateClientScopeBreakdown(entries, (entry) =>
        entry.phase ? { key: String(entry.phaseId ?? entry.phase), label: entry.phase } : null,
      ),
    [entries],
  )

  const byWorkspaceRows = useMemo(
    () =>
      aggregateClientScopeBreakdown(entries, (entry) =>
        entry.workspace ? { key: String(entry.workspaceId ?? entry.workspace), label: entry.workspace } : null,
      ),
    [entries],
  )

  const byExecutorRows = useMemo(
    () =>
      aggregateClientScopeBreakdown(entries, (entry) =>
        entry.executor ? { key: entry.executorId ?? entry.executor, label: entry.executor } : null,
      ),
    [entries],
  )

  return (
    <section ref={ref} className='space-y-3 rounded-xl border border-accent/25 bg-accent/[0.04] p-4 sm:p-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wide text-accent'>{copy.badge}</p>
          <h3 className='mt-1 text-base font-semibold text-text-primary'>{label}</h3>
          <p className='mt-0.5 text-xs text-text-muted'>{copy.subtitle}</p>
        </div>
        <button
          type='button'
          onClick={onClear}
          className='inline-flex items-center gap-1.5 rounded-lg border border-[var(--glass-border)] px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-white/[0.04] hover:text-text-primary'
        >
          <X size={14} />
          Limpar seleção
        </button>
      </div>

      <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <GestorDashboardMetricCard label='Esforço total' value={formatEffortMinutes(summary.totalEffortMinutes)} compact />
        <GestorDashboardMetricCard
          label='Horas produtivas'
          value={formatEffortMinutes(summary.productiveEffortMinutes)}
          tone='success'
          compact
        />
        <GestorDashboardMetricCard label='Tarefas distintas' value={String(summary.taskCount)} compact />
        <GestorDashboardMetricCard label='Apontamentos' value={String(summary.apontamentos)} compact />
      </div>

      <div className='grid gap-3 xl:grid-cols-2'>
        {scope === 'workspace' ? (
          <GestorDashboardBreakdownList title='Esforço por etapa' rows={byPhaseRows} />
        ) : (
          <GestorDashboardBreakdownList title='Esforço por cliente' rows={byWorkspaceRows} />
        )}
        <GestorDashboardBreakdownList title='Esforço por colaborador' rows={byExecutorRows} />
      </div>

      <GestorDashboardBreakdownList title={`Tarefas de ${label}`} rows={taskRows} onTaskOpen={onTaskOpen} />

      <GestorDashboardTimeTable entries={entries} loading={loading} onTaskOpen={onTaskOpen} />
    </section>
  )
})
